/* FormField's `layout` axis (engine tier, not in the barrel): the class
 * strings `FormField.tsx` reads for `layout="horizontal" | "card"` plus the
 * label-row wrapper that appears once a beside hint or an `actionsSlot` is
 * set. Kept out of FormField.tsx so that file stays under the 200-line cap
 * (formfield-fieldset-group already split its label row out the same way).
 * `layout="stacked"` needs nothing here — it is today's bare `w-full` /
 * `FIELDSET_RESET`, untouched in FormField.tsx. Literal class strings so
 * Tailwind sees them.
 */
import { cn } from "./utils.js";

export type FormFieldLayout = "stacked" | "horizontal" | "card";
export type FormFieldHintPlacement = "below" | "beside";

/** `horizontal`: a gutter column for the label from `sm` up — the same
 *  `9rem` gutter ConnectionsSettings and ScimOAuthClientsPanel already use
 *  for a label-left row — stacked below it. With no explicit column count,
 *  `grid` alone already stacks every child on its own row below `sm`, so
 *  mobile needs no separate "stack" class to avoid overflow at 375px; `sm`
 *  then opens the two columns and the label and the field land in them by
 *  DOM order alone. Single-control only: a group's several children would
 *  overflow past the second column (see FormField.tsx). */
export const HORIZONTAL_ROOT_CLASS =
  "grid gap-x-4 gap-y-1.5 sm:grid-cols-[9rem_1fr]";
/** Wraps the control and the message so both land in the horizontal
 *  layout's second column, stacked. */
export const HORIZONTAL_FIELD_CLASS = "min-w-0";

/** `card`: one bordered box around the label and the control — pair it with
 *  a ghost control, which has no frame of its own, or the field double-
 *  frames. `grid` (not the browser's default block layout) is what keeps
 *  `as="fieldset"`'s legend INSIDE the box instead of straddling the
 *  border — the same trick ProfileEditor's hand-built bordered fieldset
 *  already leans on (`className="grid gap-3 rounded-md border ..."`). */
export function cardBoxClass(invalid: boolean): string {
  return cn(
    "grid gap-1.5 rounded-md border bg-pb-card p-3 transition-colors",
    invalid ? "border-pb-red/60" : "border-pb-control-border",
  );
}

/** The label row once a beside hint or an `actionsSlot` is set: a flex row
 *  AROUND the `<label>` rather than inside it, so neither ever joins the
 *  control's accessible name (a `<label>`'s accessible name is computed
 *  from its own subtree only). */
export const LABEL_ROW_CLASS = "flex items-center gap-2 mb-1.5";
export const BESIDE_HINT_CLASS = "text-dense-1 text-pb-text-muted";
export const ACTIONS_SLOT_CLASS = "ms-auto";
